import React, { useState, useEffect } from 'react';
import { AlertTriangle, Mail, Phone, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../../services/apiService';

const SuspendedCompanyOverlay = ({ children }) => {
  const [suspensionData, setSuspensionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const checkSuspension = async () => {
      try {
        const response = await api.get('/oem/check-suspension/');
        setSuspensionData(response.data);
      } catch (err) {
        console.error('Error checking suspension status:', err);
        setSuspensionData({ is_suspended: false, suspended_projects: [] });
      } finally {
        setLoading(false);
      }
    };

    checkSuspension();
  }, []);

  // Show children normally if loading or not suspended
  if (loading || !suspensionData?.is_suspended) {
    return children;
  }

  const { suspended_projects } = suspensionData;

  return (
    <div className="relative min-h-screen">
      {/* Sticky Banner at top of content area */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg">
        <div className="px-6 py-3">
          {/* Main banner row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Company Suspended</h2>
                <p className="text-amber-100 text-sm">
                  Your access to OEM projects has been suspended. Contact OEM Master to resolve.
                </p>
              </div>
            </div>

            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm font-medium transition-colors"
            >
              {expanded ? 'Hide Details' : 'Show Details'}
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Expanded details */}
          {expanded && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-white/90 text-sm mb-3">Suspended from the following projects:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {suspended_projects.map((project) => (
                  <div
                    key={project.project_id}
                    className="bg-white/10 backdrop-blur-sm rounded-lg p-3"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-white" />
                      <span className="font-semibold text-white">{project.project_code}</span>
                      <span className="text-amber-100 text-sm">- {project.project_name}</span>
                    </div>
                    <p className="text-xs text-amber-100 mb-1">
                      OEM: {project.oem_company_name}
                    </p>
                    <div className="flex flex-wrap gap-3 text-xs">
                      {project.oem_contact_email && (
                        <a
                          href={`mailto:${project.oem_contact_email}`}
                          className="inline-flex items-center gap-1 text-white hover:text-amber-100 underline"
                        >
                          <Mail className="h-3 w-3" />
                          {project.oem_contact_email}
                        </a>
                      )}
                      {project.oem_contact_phone && (
                        <a
                          href={`tel:${project.oem_contact_phone}`}
                          className="inline-flex items-center gap-1 text-white hover:text-amber-100 underline"
                        >
                          <Phone className="h-3 w-3" />
                          {project.oem_contact_phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grayed out content - read only */}
      <div className="relative">
        {/* Gray overlay */}
        <div className="absolute inset-0 bg-slate-400/40 pointer-events-none z-10" />

        {/* Content - disabled interactions */}
        <div className="pointer-events-none select-none grayscale opacity-60">
          {children}
        </div>
      </div>
    </div>
  );
};

export default SuspendedCompanyOverlay;
